import { resolve } from 'styled-jsx/css';
import { colors, screen } from 'components/styles/variables';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from 'components/LanguageSwitcher';
import useConfig from 'lib/hooks/useConfig';

import Heading from 'components/Home/Heading';
import { DialogNames } from 'redux-implementations/dialog';
import useDialog from 'lib/hooks/useDialog';
import { useDispatch, useSelector } from 'react-redux';

import State from 'types/State';
import { signOut } from 'redux-implementations/auth';
import { useRouter } from 'next/router';
import tapEvent from 'lib/Tracking/Footer/TapEvent';
import useEvents from 'lib/hooks/useEvents';
import { SourcePages } from 'lib/EventSchema';
import Terms from './Terms';
import CompanyLinks from './CompanyLinks';
import SeeAll from './SeeAll';
import AppBadges from './AppBadges';
import SocialLinks from './SocialLinks';

function SignInButton(): JSX.Element {
  const { open } = useDialog(DialogNames.Auth);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isSignedIn = useSelector<State>((state) => state?.session?.isSignedIn);
  const handler = isSignedIn
    ? (): void => {
        dispatch(signOut());
      }
    : open;
  const text = isSignedIn ? t('auth.signOut') : t('auth.logIn.logIn');
  const logger = useEvents();
  const { asPath } = useRouter();

  return (
    <button
      data-testid="sign-in-button"
      onClick={(): void => {
        logger.log(tapEvent({ action: 'login', pathName: asPath as SourcePages }));
        handler();
      }}
    >
      {text}
      <style jsx>{`
        button {
          background: none;
          border-radius: 15px;
          padding: 0 10px;
        }
      `}</style>
    </button>
  );
}

/* istanbul ignore next */
const splitCss = resolve`
@media (min-width: ${screen.smMin}) {
  .split.right {
    max-width: 370px;
  }

  .split.links {
    width: 40%
  }
}
`;

function Footer(): JSX.Element {
  const { t } = useTranslation();
  const { configs } = useConfig();

  return (
    <footer>
      <div className="container">
        <Heading title={t('footer.company')} dark={true} />
        <div className="top stratum">
          <CompanyLinks className={`split links ${splitCss.className}`} />
          <div className={`split right ${splitCss.className}`}>
            <p className="about">{t('footer.description')}</p>
            <SeeAll />
            <AppBadges />
          </div>
        </div>
        <div className="stratum middle">
          <LanguageSwitcher />
          <SocialLinks />
          {configs.authEnabled && <SignInButton />}
        </div>
        <div className="stratum bottom">
          <Terms />
          <span>{`© ${new Date().getFullYear()} CarGurus, Inc. - ${t(
            'footer.allRightsReserved'
          )}`}</span>
        </div>
      </div>
      {splitCss.styles}
      <style jsx>
        {`
          footer {
            position: relative;
            background-color: ${colors.BLACK};
            color: white;
            padding-top: 15px;
            padding-bottom: 15px;
          }

          .stratum {
            margin-left: 25px;
            display: flex;
          }

          .top {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            margin-bottom: 30px;
          }

          .bottom {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            line-height: 30px;
          }

          .about {
            display: none;
          }

          .middle {
            font-size: 13px;
            font-weight: 200;
            color: ${colors.G2};
            margin-bottom: 30px;
          }

          @media (min-width: ${screen.smMin}) {
            .top {
              flex-direction: row;
              margin-bottom: 15px;
            }

            .middle {
              margin-bottom: 30px;
            }

            .bottom {
              flex-direction: row;
            }

            .about {
              display: block;
              font-size: 18px;
            }
          }
        `}
      </style>
    </footer>
  );
}

export default Footer;
